/**
 * CONN-PROBE-3 — helpers PUROS de apresentação do diagnóstico de conectividade.
 *
 * Consome o contrato tipado do backend (ConnProbeOut do F1/F2):
 *   conn = { lan:  { ok, loss, rtt, age },
 *            inet: { ok, loss, rtt, age },
 *            dns:  { ok, ms, age } }  |  null
 *
 * Observabilidade APENAS — nada aqui aciona comando/relé/reboot/alerta/watchdog.
 *
 * Janelas de "desatualizado" (stale) conservadoras, coerentes com o firmware:
 *   - LAN/INET medidos ~a cada 60 s  → stale após 180 s
 *   - DNS medido       ~a cada 300 s → stale após 600 s
 * `age` vem em segundos desde a última medição (int). `rtt`/`ms` = -1 é
 * SENTINELA de "sem resposta" (ping/resolução falhou), nunca "-1 ms".
 */

export const CONN_STALE_S = { lan: 180, inet: 180, dns: 600 };

// Estados por alvo: "ok" | "fail" | "stale" | "nodata"
// Estado do card: + "unsupported" (conn_supported=false / conn=null)
export const CONN_STATE = {
  OK: "ok",
  FAIL: "fail",
  STALE: "stale",
  NODATA: "nodata",
  UNSUPPORTED: "unsupported",
};

function _num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** age (segundos) → "agora" | "há 12 s" | "há 3 min" | "há 2 h". null/absurdo → "—". */
export function formatConnAge(age) {
  const s = _num(age);
  if (s === null || s < 0 || s > 1e9) return "—";
  if (s < 10) return "agora";
  if (s < 60) return `há ${Math.floor(s)} s`;
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  return `há ${Math.floor(s / 3600)} h`;
}

/** rtt/ms → "2 ms" | "Sem resposta" (sentinela -1) | "—" (ausente). */
export function formatRtt(rtt) {
  const n = _num(rtt);
  if (n === null) return "—";
  if (n === -1) return "Sem resposta";
  if (n < 0 || n > 60000) return "—"; // absurdo não quebra o componente
  return `${n} ms`;
}

/** loss → "0% perda" | "100% perda" | "—". */
export function formatLoss(loss) {
  const n = _num(loss);
  if (n === null || n < 0 || n > 100) return "—";
  return `${n}% perda`;
}

function _targetState(t, staleS) {
  if (!t || typeof t !== "object") return CONN_STATE.NODATA;
  const ok = typeof t.ok === "boolean" ? t.ok : null;
  const age = _num(t.age);
  if (ok === null) return CONN_STATE.NODATA;
  // falha explícita (ok=false) ou sentinela de sem-resposta manda em cima de stale
  if (ok === false || _num(t.rtt) === -1 || _num(t.ms) === -1) return CONN_STATE.FAIL;
  if (age !== null && age > staleS) return CONN_STATE.STALE;
  return CONN_STATE.OK;
}

const _STATE_LABEL = {
  ok: "OK",
  fail: "Falha",
  stale: "Dados antigos",
  nodata: "Sem dados",
  unsupported: "Não suportado",
};

export function stateLabel(state) {
  return _STATE_LABEL[state] || "—";
}

/** Classe de texto (cor) por estado — usada nos indicadores compactos L·I·D. */
export function stateTextClass(state) {
  switch (state) {
    case CONN_STATE.OK:
      return "text-noc-ok";
    case CONN_STATE.FAIL:
      return "text-noc-err";
    case CONN_STATE.STALE:
      return "text-noc-warn";
    default:
      return "text-noc-faint";
  }
}

/** Classe de badge do projeto por estado (não depende só de cor: label textual). */
export function stateBadgeClass(state) {
  switch (state) {
    case CONN_STATE.OK:
      return "badge-ok";
    case CONN_STATE.FAIL:
      return "badge-err";
    case CONN_STATE.STALE:
      return "badge-warn";
    default:
      return "badge-muted";
  }
}

/**
 * Monta a "view" pronta para render dos 3 alvos + estado geral.
 * @param conn bloco tipado (ou null)
 * @param connSupported opcional; quando ausente, deriva de conn ter ≥1 alvo.
 */
export function connProbeView(conn, connSupported) {
  const c = conn && typeof conn === "object" ? conn : null;
  const supported =
    connSupported === true ||
    (connSupported === undefined &&
      !!c &&
      (c.lan != null || c.inet != null || c.dns != null));

  if (!supported) {
    return { supported: false, overall: CONN_STATE.UNSUPPORTED, lan: null, inet: null, dns: null };
  }

  const mk = (t, kind, staleS) => {
    const state = _targetState(t, staleS);
    const view = { state, label: stateLabel(state), badge: stateBadgeClass(state), age: formatConnAge(t && t.age) };
    if (kind === "dns") {
      view.rtt = formatRtt(t && t.ms);
    } else {
      view.rtt = formatRtt(t && t.rtt);
      view.loss = formatLoss(t && t.loss);
    }
    return view;
  };

  const lan = mk(c ? c.lan : null, "target", CONN_STALE_S.lan);
  const inet = mk(c ? c.inet : null, "target", CONN_STALE_S.inet);
  const dns = mk(c ? c.dns : null, "dns", CONN_STALE_S.dns);

  // Estado geral p/ chip/marker: prioridade Falha > Dados antigos > OK > Sem dados.
  const states = [lan.state, inet.state, dns.state];
  let overall = CONN_STATE.NODATA;
  if (states.includes(CONN_STATE.FAIL)) overall = CONN_STATE.FAIL;
  else if (states.includes(CONN_STATE.STALE)) overall = CONN_STATE.STALE;
  else if (states.includes(CONN_STATE.OK)) overall = CONN_STATE.OK;

  return { supported: true, overall, lan, inet, dns };
}

/**
 * Resumo textual compacto p/ tooltip do marker do mapa (HTML-string, sem React).
 * @returns { text, cls, supported }  cls ∈ "err" | "warn" | null (classe do tooltip).
 */
export function connSummary(conn, connSupported) {
  const v = connProbeView(conn, connSupported);
  if (!v.supported) {
    return { text: "Diagnóstico de conectividade indisponível", cls: null, supported: false };
  }
  const cls =
    v.overall === CONN_STATE.FAIL ? "err" : v.overall === CONN_STATE.STALE ? "warn" : null;
  return {
    text: `Conexão: LAN ${v.lan.label} · INET ${v.inet.label} · DNS ${v.dns.label}`,
    cls,
    supported: true,
  };
}

/** Resumo compacto p/ chip da lista / marker do mapa. */
export function connChip(conn, connSupported) {
  const v = connProbeView(conn, connSupported);
  if (!v.supported) return { state: CONN_STATE.UNSUPPORTED, label: "—", badge: "badge-muted", title: "Sem diagnóstico de conectividade" };
  const map = {
    ok: "OK",
    fail: "Internet/rede falhou",
    stale: "Dados antigos",
    nodata: "Sem dados",
  };
  return {
    state: v.overall,
    label: map[v.overall] || "—",
    badge: stateBadgeClass(v.overall),
    title: `Rede local: ${v.lan.label} · Internet: ${v.inet.label} · DNS: ${v.dns.label}`,
  };
}

// ============================================================================
// CONN-PROBE-4-FE — helpers PUROS do HISTÓRICO de conectividade (timeline).
//
// Consomem o contrato do endpoint GET /devices/{id}/conn-history:
//   { supported, window_hours, bucket_minutes,
//     points: [ { t, samples, gap, lan, inet, dns } ],
//     summary: { coverage_pct, heartbeat_gap_buckets, gaps_gt_90s,
//                longest_heartbeat_gap_s, lan_ok_pct, inet_ok_pct, dns_ok_pct,
//                dns_fails, samples, first_at, last_at,
//                lan|inet|dns: { ok_pct, availability_pct, fail_buckets,
//                  partial_buckets, missing_buckets, longest_failure_minutes,
//                  consecutive_fail_buckets, first_failure_at, last_failure_at } } }
//
// Contrato de timeline NÃO ambíguo (espelha o backend F4A):
//   ponto gap=true (+ alvos null) → SEM heartbeat no bucket    (NUNCA "falha")
//   alvo state="missing"          → houve HB, alvo não veio     (NUNCA "falha")
// Observabilidade APENAS — nada aqui aciona comando/relé/reboot/alerta/watchdog.
// ============================================================================

function _int(v) {
  const n = _num(v);
  return n === null ? 0 : Math.trunc(n);
}

// Períodos oferecidos pelo seletor (rótulo → hours do endpoint).
export const CONN_HISTORY_PERIODS = [
  { key: "1h", label: "1 h", hours: 1 },
  { key: "6h", label: "6 h", hours: 6 },
  { key: "24h", label: "24 h", hours: 24 },
  { key: "7d", label: "7 dias", hours: 168 },
];
export const CONN_HISTORY_DEFAULT_HOURS = 6;

/** Normaliza `hours` p/ inteiro em [1,168] (faixa do endpoint); inválido → 6.
 *  null/""/tipo inesperado → default (Number(null)===0 seria coerção enganosa);
 *  número real 0/negativo → clamp p/ 1; >168 → 168. */
export function normalizeHistoryHours(h) {
  if (h == null || h === "" || (typeof h !== "number" && typeof h !== "string")) {
    return CONN_HISTORY_DEFAULT_HOURS;
  }
  const n = Number(h);
  if (!Number.isFinite(n)) return CONN_HISTORY_DEFAULT_HOURS;
  const i = Math.trunc(n);
  if (i < 1) return 1; // clamp inferior (endpoint ge=1 → nunca 422)
  if (i > 168) return 168; // clamp superior (endpoint le=168)
  return i;
}

/** Monta o path do endpoint (device_id encodado + hours normalizado). Puro. */
export function connHistoryPath(deviceId, hours) {
  const enc = encodeURIComponent(String(deviceId || ""));
  return `/api/admin/devices/${enc}/conn-history?hours=${normalizeHistoryHours(hours)}`;
}

/** Guard de resposta obsoleta: só aplica a resposta cuja seq é a mais recente. */
export function shouldApplyResponse(responseSeq, currentSeq) {
  return responseSeq === currentSeq;
}

// Estados de célula da timeline (bucket × alvo).
export const CONN_HIST_STATE = {
  OK: "ok",
  FAIL: "fail",
  PARTIAL: "partial",
  MISSING: "missing",
  GAP: "gap",
  NODATA: "nodata",
};

const _HIST_META = {
  // color = hex do token (fundo da célula); text = classe de cor; glyph = símbolo
  // (não depende SÓ de cor). gap/nodata não têm cor de preenchimento (hachura/traço).
  ok: { label: "Saudável", color: "#35E8A4", text: "text-noc-ok", glyph: "●" },
  fail: { label: "Falha", color: "#FF5063", text: "text-noc-err", glyph: "✕" },
  partial: { label: "Parcial", color: "#FFB224", text: "text-noc-warn", glyph: "▲" },
  missing: { label: "Não reportado", color: "#7E93AE", text: "text-noc-muted", glyph: "–" },
  gap: { label: "Sem heartbeat", color: null, text: "text-noc-faint", glyph: "·" },
  nodata: { label: "Sem dado", color: null, text: "text-noc-faint", glyph: "·" },
};

export function connHistStateMeta(kind) {
  return _HIST_META[kind] || _HIST_META.nodata;
}
export function connHistStateLabel(kind) {
  return connHistStateMeta(kind).label;
}

// Legenda compacta (ordem de leitura operacional).
export const CONN_HIST_LEGEND = [
  { kind: "ok", label: "Saudável" },
  { kind: "fail", label: "Falha" },
  { kind: "partial", label: "Parcial" },
  { kind: "missing", label: "Não reportado" },
  { kind: "gap", label: "Sem heartbeat" },
];

export const CONN_AVAILABILITY_HELP =
  "Disponibilidade considera somente amostras com resultado válido. Períodos " +
  "sem heartbeat, alvo ausente ou resultado desconhecido não entram no cálculo.";

/**
 * Classifica a célula (bucket × alvo) da timeline. NUNCA mascara gap/missing
 * como falha.
 *   point.gap === true          → GAP     (sem heartbeat)
 *   alvo null/ausente           → MISSING (houve HB, alvo não veio — defensivo)
 *   alvo.state ∈ ok|fail|partial|missing → repassa
 * @param point objeto de points[] (ou null)
 * @param target "lan" | "inet" | "dns"
 */
export function connHistCellState(point, target) {
  if (!point || typeof point !== "object") return CONN_HIST_STATE.NODATA;
  if (point.gap === true) return CONN_HIST_STATE.GAP;
  const t = point[target];
  if (t == null || typeof t !== "object") return CONN_HIST_STATE.MISSING;
  switch (t.state) {
    case "fail":
      return CONN_HIST_STATE.FAIL;
    case "partial":
      return CONN_HIST_STATE.PARTIAL;
    case "missing":
      return CONN_HIST_STATE.MISSING;
    case "ok":
      return CONN_HIST_STATE.OK;
    default:
      return CONN_HIST_STATE.NODATA;
  }
}

/** Percentual 0-100 → "100%" | "99,5%"→"99.5%" | "—" (null/inválido). */
export function formatPct(n) {
  const v = _num(n);
  if (v === null || v < 0 || v > 100) return "—";
  const r = Math.round(v * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}

/** Minutos (float) → "0 min" | "12 min" | "1.5 min" | "—" (null/negativo). */
export function formatMinutes(n) {
  const v = _num(n);
  if (v === null || v < 0) return "—";
  const r = Math.round(v * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)} min`;
}

/** rtt/ms médio → "43 ms" | "Sem resposta" (-1) | "—" (null/ausente/absurdo).
 *  NUNCA transforma ausência de RTT em 0. */
export function formatLatencyAvg(n) {
  const v = _num(n);
  if (v === null) return "—";
  if (v === -1) return "Sem resposta";
  if (v < 0 || v > 60000) return "—";
  const r = Math.round(v * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)} ms`;
}

/** ISO-8601 UTC → data+hora no fuso LOCAL do navegador. null/inválido → "—". */
export function formatDateTimeLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // pt-BR + fuso LOCAL do navegador (mesma convenção do fmtTime do DeviceDetail).
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** ISO → hora local curta (rótulo de eixo). null/inválido → "—". */
export function formatClockLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/** Tom (cor) da disponibilidade. null → "muted". */
export function availabilityTone(pct) {
  const v = _num(pct);
  if (v === null) return "muted";
  if (v >= 99.5) return "ok";
  if (v >= 95) return "warn";
  return "err";
}

/**
 * View pronta do resumo por alvo (summary.lan|inet|dns). null → null (alvo
 * nunca reportado no período). Só campos do contrato; nada sensível.
 */
export function connHistTargetSummary(t) {
  if (!t || typeof t !== "object") return null;
  return {
    availability: formatPct(t.availability_pct),
    availabilityRaw: _num(t.availability_pct),
    tone: availabilityTone(t.availability_pct),
    okPct: formatPct(t.ok_pct),
    failBuckets: _int(t.fail_buckets),
    partialBuckets: _int(t.partial_buckets),
    missingBuckets: _int(t.missing_buckets),
    longestFailure: formatMinutes(t.longest_failure_minutes),
    consecutiveFail: _int(t.consecutive_fail_buckets),
    firstFailure: t.first_failure_at ? formatDateTimeLocal(t.first_failure_at) : null,
    lastFailure: t.last_failure_at ? formatDateTimeLocal(t.last_failure_at) : null,
  };
}

/** Latência média do bucket p/ um alvo (dns usa ms_avg; lan/inet usam rtt_avg). */
export function connHistPointLatency(point, target) {
  const t = (point && point[target]) || null;
  if (!t || typeof t !== "object") return "—";
  return target === "dns" ? formatLatencyAvg(t.ms_avg) : formatLatencyAvg(t.rtt_avg);
}

/** samples → "1 amostra" | "N amostras" | "—" (null/negativo). */
export function formatSamples(n) {
  const v = _num(n);
  if (v === null || v < 0) return "—";
  const i = Math.trunc(v);
  return i === 1 ? "1 amostra" : `${i} amostras`;
}

/** fails → "1 falha" | "N falhas" | "—" (null/negativo). */
export function formatFails(n) {
  const v = _num(n);
  if (v === null || v < 0) return "—";
  const i = Math.trunc(v);
  return i === 1 ? "1 falha" : `${i} falhas`;
}

/** idade (segundos) → "14 s" | "3 min" | "2 h" | "—". Sem "há" (é rótulo de métrica). */
export function formatAgeSeconds(n) {
  const v = _num(n);
  if (v === null || v < 0 || v > 1e9) return "—";
  if (v < 60) return `${Math.trunc(v)} s`;
  if (v < 3600) return `${Math.floor(v / 60)} min`;
  return `${Math.floor(v / 3600)} h`;
}

/** Período do bucket "14:00–14:15" (início local + bucketMinutes). Sem largura
 *  conhecida → só o início. null/inválido → "—". */
export function connHistBucketPeriod(point, bucketMinutes) {
  if (!point || !point.t) return "—";
  const start = new Date(point.t);
  if (Number.isNaN(start.getTime())) return "—";
  const startStr = formatClockLocal(point.t);
  const bm = Number(bucketMinutes);
  if (!Number.isFinite(bm) || bm <= 0) return startStr;
  const end = new Date(start.getTime() + bm * 60000);
  return `${startStr}–${formatClockLocal(end.toISOString())}`;
}

// Linhas de métrica (label→valor) de um alvo. dns NÃO tem perda. Valor ausente →
// "—" (nunca 0 forjado); rtt/ms médios excluem a sentinela -1 no backend.
function _metricRows(t, isDns) {
  const o = t && typeof t === "object" ? t : {};
  if (isDns) {
    return [
      { label: "Tempo médio", value: formatLatencyAvg(o.ms_avg) },
      { label: "p95", value: formatLatencyAvg(o.ms_p95) },
      { label: "Amostras", value: formatSamples(o.samples) },
      { label: "Falhas", value: formatFails(o.fails) },
      { label: "Idade máxima", value: formatAgeSeconds(o.age_max) },
    ];
  }
  return [
    { label: "RTT médio", value: formatLatencyAvg(o.rtt_avg) },
    { label: "RTT p95", value: formatLatencyAvg(o.rtt_p95) },
    { label: "Perda média", value: formatPct(o.loss_avg) },
    { label: "Perda máxima", value: formatPct(o.loss_max) },
    { label: "Amostras", value: formatSamples(o.samples) },
    { label: "Falhas", value: formatFails(o.fails) },
    { label: "Idade máxima", value: formatAgeSeconds(o.age_max) },
  ];
}

/**
 * Detalhe de um alvo p/ o tooltip/faixa: estado + métricas do contrato.
 * gap/missing NÃO inventam métricas (metrics=[]) e NUNCA são "falha".
 */
export function connHistTargetDetail(point, targetKey, label) {
  const kind = connHistCellState(point, targetKey);
  const base = { key: targetKey, label, kind, stateLabel: connHistStateLabel(kind) };
  if (kind === CONN_HIST_STATE.GAP) return { ...base, metrics: [], note: null };
  if (kind === CONN_HIST_STATE.MISSING) {
    return {
      ...base,
      metrics: [],
      note: "Heartbeat recebido, mas este alvo não veio na amostra (não é necessariamente falha).",
    };
  }
  return { ...base, metrics: _metricRows(point[targetKey], targetKey === "dns"), note: null };
}

/**
 * Detalhe de um bucket p/ a faixa (mouse + toque + teclado). SÓ expõe
 * t/estado/métricas do contrato — nada de payload/segredo.
 * @param bucketMinutes largura do bucket (data.bucket_minutes) p/ o período.
 */
export function connHistPointDetail(point, bucketMinutes) {
  if (!point || typeof point !== "object") return null;
  const time = formatDateTimeLocal(point.t);
  const period = connHistBucketPeriod(point, bucketMinutes);
  if (point.gap === true) {
    return {
      time,
      period,
      gap: true,
      note: "Nenhum heartbeat recebido neste intervalo.",
      targets: [],
    };
  }
  const targets = [
    ["lan", "Rede local"],
    ["inet", "Internet"],
    ["dns", "DNS"],
  ].map(([key, label]) => connHistTargetDetail(point, key, label));
  return { time, period, gap: false, targets };
}

/** Texto do tooltip/aria de uma célula (período + estado + métricas). Sanitizado;
 *  no título nativo, linhas ausentes ("—") são omitidas p/ concisão. */
export function connHistCellTitle(point, target, targetLabel, bucketMinutes) {
  const period = connHistBucketPeriod(point, bucketMinutes);
  const kind = connHistCellState(point, target);
  if (kind === CONN_HIST_STATE.GAP) {
    return `${period} — Sem heartbeat: nenhum heartbeat recebido neste intervalo.`;
  }
  if (kind === CONN_HIST_STATE.MISSING) {
    return `${period} — ${targetLabel}: Não reportado (houve heartbeat; este alvo não veio na amostra — não é necessariamente falha).`;
  }
  const rows = _metricRows(point[target], target === "dns")
    .filter((r) => r.value !== "—")
    .map((r) => `${r.label}: ${r.value}`);
  const head = `${period} — ${targetLabel}: ${connHistStateLabel(kind)}`;
  return rows.length ? `${head}\n${rows.join("\n")}` : head;
}
